import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import {
    createHandlerChainRunner,
    Handler
} from "@insite/client-framework/HandlerCreator";
import { ApiParameter } from "@insite/client-framework/Services/ApiService";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";

import { ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";

export interface GetProductCollectionParameter extends ApiParameter {
    productCollection: ProductModel[];
    listType: string;
    carouselTitle: string;
    productInfoCollection?: ProductInfo[];
}
type Parameter = GetProductCollectionParameter;
interface Impression {
    name: string;
    id: string;
    price: number;
    brand: string;
    category: string;
    list: string;
    position: number;
    stock: string;
    stockqty: number;
    vendorstockstatus: string,
    vendorstockqty: number
}
interface Props {
    products?: ProductModel[];
    productInfos?: ProductInfo[];
    pricingLoaded?: true;
    impressions?: Impression[];

}
type HandlerType = Handler<Parameter, Props>;

export const LoadProducts: HandlerType = props => {

    const parameter = props.parameter;
    if (!parameter) {
        return false;
    }
    props.products = parameter.productCollection;
    props.productInfos = parameter.productInfoCollection;
};
export const SetUpProductInfos: HandlerType = props => {

    if (!props.products) {
        return;
    }

    if(!props.productInfos) {
        props.productInfos = [];
        for (const product of props.products) {
            props.productInfos.push(createFromProduct(product));
        }
    }
};
export const LoadProductInventory: HandlerType = props => {
    if(!props.productInfos?.some(a=>a.inventory)) {
        const productIds = props.parameter.productCollection.map(o => o.id);
        productIds?.length && props.dispatch(
            loadRealTimeInventory({
                productIds: productIds as string[],
                expand: ["warehouses"],
                onSuccess: realTimeInventory => {
                    realTimeInventory?.realTimeInventoryResults?.forEach(inventory => {
                        props.productInfos?.forEach(productInfo => {
                            if (productInfo.productId === inventory.productId) {
                                productInfo.inventory = inventory;
                            }
                        });
                    });

                },
            }),
        );
    }
}
export const LoadRealTimePrices: HandlerType = props => {

    if (
        !props.productInfos ||
        props.productInfos.length === 0
    ) {
        return;
    }
    if(!props.productInfos.some(a=>a.pricing)) {
        props.dispatch(
            loadRealTimePricing({
                productPriceParameters: props.productInfos,
                onSuccess: realTimePricing => {
                    realTimePricing.realTimePricingResults?.forEach((pricing: ProductPriceDto) => {
                        props.productInfos?.forEach(productInfo => {
                            if (productInfo.productId === pricing.productId) {
                                productInfo.pricing = pricing;
                            }
                        });
                    });
                }
            }),
        );
    }
};
export const SetProductImpression: HandlerType = props => {
    const impressions: Array<Impression> = [];
    // let impressions = Impression[];
    props.products?.forEach(async (product, index) => {
        const productInfo = props.productInfos?.find(p => p.productId === product.id);
        if (!productInfo) {
            return;
        }
        await waitFor(() => !!productInfo?.pricing && !!productInfo?.inventory);


        const productPrice = productInfo?.pricing?.actualPrice;
        const qtyOnHand = productInfo?.inventory?.qtyOnHand ?? 0;

        const description = product.productTitle ?? "";
        const productNumber = product.productNumber ?? "";
        const brandName = product.brand?.name ?? "";
        const currentUrl = window.document.URL;
        const condition=product?.properties?.condition??"";

        const pageSourceResult = props.parameter.carouselTitle ? props.parameter.carouselTitle : currentUrl.toLowerCase().includes("search") ? 'Search Results'
            : currentUrl.toLowerCase().includes("category/all/")
                ? "Brand Results"
                : currentUrl.toLowerCase().includes("category/all")
                    ? "Brand Results"
                    : currentUrl.toLowerCase().includes("brand")
                        ? "Brand Results"
                        : currentUrl.toLowerCase().includes("category") ? 'Category Results' : "Product List";
        // Condition to show stock as "DirectShip" when total qty<=0 and isDirectShipFromManufacturer is true
        const availabilityMsg = Number(qtyOnHand) > 0 
                                ? "In Stock" 
                                : product?.properties?.isDirectShipFromManufacturer.toLowerCase() === "true"
                                    ?"DirectShip" : "Out Of Stock";


        const categoryPath = currentUrl.replace(/\/$/, "");
        const fragmentArr = categoryPath.split("/");
        const categoryNameURI = fragmentArr[fragmentArr.length - 1];
        let categoryName = categoryNameURI.split("-").join(" ");
        categoryName = categoryName.split('?')[0];

        if (categoryName === "" || categoryName.toLocaleLowerCase().startsWith("search")) {
            categoryName = "product";
        }

        let vendorStockStatus = "";
        let vendorStockQty = 0;
        // const [vendorStockStatus, setVendorStockStatus] = useState("");
        // const [vendorStockQty, setvendorStockQty] = useState(0);

        if (productInfo?.inventory?.inventoryWarehousesDtos) {
            const warehouseDto = productInfo?.inventory?.inventoryWarehousesDtos[0].warehouseDtos;
            if (warehouseDto) {
                for (const warehouseData of warehouseDto) {

                    if (warehouseData.name === "Vendor Stock") {
                        // setvendorStockQty(warehouseData.qty);
                        vendorStockQty = warehouseData.qty;
                    }

                    if (warehouseData.qty > 0 && warehouseData.name === "Blind Ship") {
                        // setVendorStockStatus("blindship");
                        vendorStockStatus = "blindship";
                        break;
                    }

                    if (product.properties.isDirectShipFromManufacturer?.toLowerCase() === "false") {
                        if ((warehouseData.qty > 0 && warehouseData.name === "Vendor Stock") || (warehouseData.qty === 0 && warehouseData.name === "Blind Ship")) {
                            // setVendorStockStatus("crossdock");
                            vendorStockStatus = "crossdock";
                            break;
                        }
                    }
                }
            }
        }

        if (product.properties.isDirectShipFromManufacturer?.toLowerCase() === "true" && vendorStockStatus === "") {
            // setVendorStockStatus("directship");
            vendorStockStatus = "directship";
        }

        const impression = {
            'name': description,
            'id': productNumber,
            'price': productPrice,
            'brand': brandName,
            'condition':condition,
            'category': categoryName,
            'list': pageSourceResult,
            'position': index,
            'stock': availabilityMsg,
            'stockqty': qtyOnHand,
            'vendorstockstatus': vendorStockStatus,
            'vendorstockqty': vendorStockQty
        } as Impression;
        impressions?.push(impression);

    });
    props.impressions = impressions;
};

const normalizeImpressions = (impressions: any[]) =>
    JSON.stringify(impressions.map(p => JSON.stringify(p)).sort());

export const setImpression: HandlerType = (props) => {
    setTimeout(() => {
        const dataLayer = (window as any).dataLayer || [];
        const currentImpressions = props.impressions;

        if (!currentImpressions?.length) return;

        const historyEvents = dataLayer.filter(
            (item: Record<string, any>) =>
                item.event === 'gtm.historyChange' || item.event === 'gtm.historyChange-v2'
        );

        const lastHistoryEventId =
            historyEvents.length > 0
                ? historyEvents[historyEvents.length - 1]['gtm.uniqueEventId']
                : -1;

        const normalizedCurrent = normalizeImpressions(currentImpressions);

        const productImpressions = dataLayer.filter(
            (item: Record<string, any>) =>
                item.event === 'productImpressions' &&
                item['gtm.uniqueEventId'] > lastHistoryEventId
        );

        for (const pi of productImpressions) {
            const prevImpressions = pi.ecommerce?.impressions || [];
            if (normalizeImpressions(prevImpressions) === normalizedCurrent) {
                return;
            }
        }
        dataLayer.push({
            event: "productImpressions",
            ecommerce: {
                currencyCode: 'USD',
                impressions: currentImpressions,
            },
        });
    }, 2000);
}

export const chain = [LoadProducts, SetUpProductInfos, LoadProductInventory, LoadRealTimePrices, SetProductImpression, setImpression];

const logProductImpression = createHandlerChainRunner(chain, "LogProductImpression");

export default logProductImpression;
